/* cc-gallery-variant-sync — see snippets/cc-gallery-variant-sync.liquid.
   Media id (from the tapped thumbnail / swiped slide) → variant →
   check the matching pill inputs / set the dropdowns and dispatch
   `change`, which runs Dawn's normal variant pipeline (VariantSelects
   publishes optionValueSelectionChange; product-info refreshes price,
   URL, stock, media). Dawn's own media update then lands on the image
   that's already active, so no feedback loop — plus we skip when the
   variant is already selected. */

(() => {
  // ---- Fixed thumbnail positions (Tony 2026-07-06) ----
  // Dawn moves the selected variant's media to the FRONT on variant
  // change (media-gallery.setActiveMedia prepend + product-info
  // updateMedia re-sorting to the server's variant-first order), so
  // thumbnails shuffle every selection. Tony wants positions parked:
  // the ring alone marks the active thumb. Override updateMedia with
  // a lean version (activate + sync zoom modal, never reorder) and
  // coerce prepend off. Trade-off documented in CONVENTIONS: skips
  // Dawn's add/remove-media logic used only by combined listings.
  // Sticky-bar-driven picks (stack cards / bar selects / sheet) stamp the
  // body with data-cc-bar-pick — the page must not move for those. Two
  // Dawn behaviors are suppressed while the stamp is fresh (both scroll
  // the page up from the bar, Tony 2026-08-06): media-gallery's scroll-
  // photo-into-view (wrapper below) and product-info's post-render
  // re-focus of the clicked option input (renderProductInfo wrapper —
  // focus() scrolls the off-screen buy area into view AND steals focus
  // out of the bar). No stamp = stock Dawn behavior.
  const ccBarPickFresh = () =>
    Date.now() - Number(document.body.dataset.ccBarPick || 0) < 4000;

  const PI = customElements.get('product-info');
  if (PI) {
    PI.prototype.updateMedia = function (html, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;
      this.querySelector('media-gallery')?.setActiveMedia?.(
        `${this.dataset.section}-${variantFeaturedMediaId}`,
        false
      );
      const modalContent = this.productModal?.querySelector('.product-media-modal__content');
      const newModalContent = html.querySelector('product-modal .product-media-modal__content');
      if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
    };

    const origRender = PI.prototype.renderProductInfo;
    PI.prototype.renderProductInfo = function (args) {
      if (ccBarPickFresh() && args && args.targetId) {
        // targetId's only in-render use is the focus() call — pointing it
        // at a non-existent id turns the re-focus into a no-op while
        // leaving requestUrl/callback untouched
        args = Object.assign({}, args, { targetId: 'cc-no-refocus' });
      }
      return origRender.call(this, args);
    };
  }
  const MG = customElements.get('media-gallery');
  if (MG) {
    const orig = MG.prototype.setActiveMedia;
    MG.prototype.setActiveMedia = function (mediaId) {
      // prepend coercion only — the bar-pick page-scroll suppression
      // lives as a CC-EDIT inside media-gallery.js itself (a cancel from
      // out here can't abort the queued smooth scroll reliably)
      return orig.call(this, mediaId, false);
    };
  }

  const productInfo = document.querySelector("product-info[id^='MainProduct']");
  const gallery = document.querySelector('media-gallery');
  const mapEl = document.getElementById('cc-media-variant-map');
  if (!productInfo || !gallery || !mapEl) return;

  let map;
  try {
    map = JSON.parse(mapEl.textContent);
  } catch (e) {
    return;
  }
  if (!map || !Object.keys(map).length) return;

  const sectionId = productInfo.dataset.section;

  const currentVariantId = () => {
    try {
      return JSON.parse(
        productInfo.querySelector('variant-selects [data-selected-variant]').textContent
      ).id;
    } catch (e) {
      return null;
    }
  };

  let suppress = false;

  const selectForMedia = (domMediaId) => {
    if (suppress || !domMediaId) return;
    const variant = map[String(domMediaId).replace(sectionId + '-', '')];
    if (!variant || variant.id === currentVariantId()) return;

    const selects = productInfo.querySelector('variant-selects');
    if (!selects) return;

    suppress = true;
    const groups = selects.querySelectorAll('.product-form__input');
    variant.options.forEach((value, i) => {
      const group = groups[i];
      if (!group) return;
      const dropdown = group.querySelector('select');
      if (dropdown) {
        if (dropdown.value !== value) {
          dropdown.value = value;
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      const radio = Array.from(group.querySelectorAll("input[type='radio']")).find(
        (r) => r.value === value
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    setTimeout(() => (suppress = false), 400);
  };

  // Tap: thumbnail buttons (delegate on the gallery — it survives
  // product-info's partial re-renders)
  gallery.addEventListener('click', (e) => {
    const item = e.target.closest('[data-target]');
    if (!item || !gallery.contains(item)) return;
    // after media-gallery.js has switched the active media
    setTimeout(() => selectForMedia(item.dataset.target), 0);
  });

  // Swipe (mobile slider): slideChanged fires with the new slide
  const viewer = gallery.querySelector("slider-component[id^='GalleryViewer']");
  if (viewer) {
    let timer;
    viewer.addEventListener('slideChanged', (e) => {
      const mediaId = e.detail?.currentElement?.dataset?.mediaId;
      if (!mediaId) return;
      clearTimeout(timer);
      timer = setTimeout(() => selectForMedia(mediaId), 200);
    });
  }
})();
